"use client";

import dynamic from "next/dynamic";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import GridColumn from "@/components/common/grid-column/grid-column";
import GridRow from "@/components/common/grid-row/grid-row";
import { usePerformance } from "@/contexts/performance-context";
import { useLayout } from "@/contexts/layout-context";
import type { Page } from "@/sanity/sanity.types";

const AiCalculatorPopup = dynamic(
  () => import("@/components/ai-calculator-popup/ai-calculator-popup"),
  {
    ssr: false,
  },
);

interface IResourceClientContentProps {
  beforeQueryTime: number;
  afterQueryTime: number;
  aiCalculatorData?: Page;
  calculatorArticleSlugs?: string[];
}

/**
 * Runs the article's client-only mount work (performance timings + registering
 * the AI calculator data) and renders the AI calculator popup when the current
 * article is not itself a calculator page.
 */
const ResourceClientContent = ({
  beforeQueryTime,
  afterQueryTime,
  aiCalculatorData,
  calculatorArticleSlugs,
}: IResourceClientContentProps) => {
  const { checkTimeElapsed } = usePerformance();
  const { setAiCalculatorData } = useLayout();
  const pathname = usePathname();

  useEffect(() => {
    checkTimeElapsed(beforeQueryTime, "Resource page before query started");
    checkTimeElapsed(afterQueryTime, "Resource page after query completed");
    setAiCalculatorData(aiCalculatorData);
  }, []);

  const showAiPopup =
    !calculatorArticleSlugs ||
    !calculatorArticleSlugs.some((slug) => `/learn/${slug}` === pathname);

  if (!showAiPopup) {
    return null;
  }

  return (
    <GridRow>
      <GridColumn lg={12}>
        <AiCalculatorPopup />
      </GridColumn>
    </GridRow>
  );
};

export default ResourceClientContent;
